import React, { useMemo } from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';
import { defineMessage, useIntl } from 'react-intl';

import { AnalyticsSideNavItem, useSendSideNavItemClick } from '@townsquare/analytics-nav4';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import { type RenderLegacyTopNavContent, SideNavItem } from '@townsquare/griffin-navigation/types';
import { NavPlatformSideNav } from '@townsquare/nav-platform-integration/NavPlatformSideNav';
import { TOWNSQUARE_APP_SWITCHER_START_SLOTS } from '@townsquare/nav-platform-integration/app-switcher-slot';
import {
	TownsquareGlobalRoot,
	useTownsquareLegacyTopNavRenderer,
} from '@townsquare/nav-platform-integration/global-root';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { useQueryParams } from '@townsquare/router/use-query-params';
import { useRouter } from '@townsquare/router/useRouter';
import { teamsAppForYouRoute } from '@townsquare/routes/teams-app-for-you-view';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { fg } from '@townsquare/stat-sig/gate';
import { useIsSettingsRoute } from '@townsquare/teams-app-config/useIsSettingsRoute';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../griffin-navigation/ProductHome';
import { SettingsSidebarNavigation } from '../griffin-navigation/Settings/SettingsSidebarNavigation';
import { useSideNavAppItemsForNavPlatform } from '../griffin-navigation/use-side-nav-app-items';
import { useSideNavItems } from '../griffin-navigation/use-side-nav-items';
import { useUniversalCreateTeamsItems } from '../griffin-navigation/use-universal-create-teams-items';
import { GriffinLayout } from '../layouts/Home/GriffinLayout';

import { isTeamsForYouSelected } from './forYouSelection';

const TEAMS_APP = 'teams';
const TEAMS_CUSTOMIZATION_STORE = 'local';

export function GriffinRoute(): React.JSX.Element {
	return fg('navx-global-top-nav-teams') ? <GriffinGlobalRoute /> : <GriffinLegacyRoute />;
}

export function GriffinLegacyRoute(): React.JSX.Element {
	return (
		<Root isSideNavShortcutEnabled>
			<SiteBannerProvider>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

export function GriffinGlobalRoute(): React.JSX.Element {
	const { formatMessage } = useIntl();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<SidebarPanelContext>
			<GlobalAppShortcutsProvider>
				<TownsquareGlobalRoot
					app={TEAMS_APP}
					appLabel={formatMessage(teamsAppLabelMessage)}
					customizationStore={TEAMS_CUSTOMIZATION_STORE}
					TopNav={TeamsGlobalTopNav}
					sideNav={<TeamsGlobalSideNav />}
				>
					<SiteBannerProvider>
						<GriffinRouteContent SidebarPanel={SidebarPanel} />
					</SiteBannerProvider>
				</TownsquareGlobalRoot>
			</GlobalAppShortcutsProvider>
		</SidebarPanelContext>
	);
}

function GriffinRouteContent({ SidebarPanel }: { SidebarPanel: React.ComponentType }) {
	const noWorkspaceExperience = useNoWorkspaceExperience();

	return (
		<>
			<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

function TeamsGlobalSideNav() {
	const sidebarItems = useSideNavItems();
	const isSettingsRoute = useIsSettingsRoute();

	return isSettingsRoute ? (
		<SettingsSidebarNavigation />
	) : (
		<PlatformSideNav sidebarItems={sidebarItems} />
	);
}

function TeamsGlobalTopNav() {
	const renderLegacyTopNavContent = useTownsquareLegacyTopNavRenderer(
		TOWNSQUARE_APP_SWITCHER_START_SLOTS,
	);

	return <GriffinTopSection renderLegacyTopNavContent={renderLegacyTopNavContent} />;
}

const GriffinTopSection = ({
	renderLegacyTopNavContent,
}: {
	/** Only passed by the gated GlobalRoot top nav. */
	renderLegacyTopNavContent?: RenderLegacyTopNavContent;
}) => {
	const { items: universalCreateItems, compact } = useUniversalCreateTeamsItems();

	return (
		<GriffinTopNavigation
			renderProductHome={() => (
				<AppHomeWrapper>
					<ProductHome />
				</AppHomeWrapper>
			)}
			universalCreateItems={universalCreateItems}
			compact={compact}
			UNSAFE__renderLegacyTopNavContent={renderLegacyTopNavContent}
		/>
	);
};

function GriffinRouteComponent() {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const sidebarItems = useSideNavItems();

	const isSettingsRoute = useIsSettingsRoute();

	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<>
			<SidebarPanelContext>
				<GriffinTopSection />
				{isSettingsRoute ? (
					<SettingsSidebarNavigation />
				) : (
					<PlatformSideNav sidebarItems={sidebarItems} />
				)}
				<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			</SidebarPanelContext>
			{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

function PlatformSideNav({ sidebarItems }: { sidebarItems: SideNavItem[] }) {
	const appShortcuts = useSideNavAppItemsForNavPlatform();
	const { formatMessage } = useIntl();
	const routeDependencies = useRouteDependencies();
	const [routerStore, routerActions] = useRouter();
	const sendSideNavItemClick = useSendSideNavItemClick();
	const queryParams = useQueryParams();

	const forYouConfig = useMemo<ForYouConfig>(() => {
		const newUrl = generatePath(teamsAppForYouRoute, routeDependencies);
		const newConfig: ForYouConfig = {
			forYouItem: {
				url: newUrl,
				isSelected: isTeamsForYouSelected(routerStore.location.pathname, queryParams),
				onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
					event.preventDefault();
					routerActions.push(newUrl);
					sendSideNavItemClick('navigationItem', AnalyticsSideNavItem.TEAMS, {
						screen: routerStore.route.name,
					});
				},
			},
		};
		return newConfig;
	}, [
		routerStore.location.pathname,
		queryParams,
		routeDependencies,
		routerActions,
		routerStore.route.name,
		sendSideNavItemClick,
	]);

	return (
		<NavPlatformSideNav
			app={TEAMS_APP}
			appLabel={formatMessage(teamsAppLabelMessage)}
			appItems={sidebarItems}
			appShortcuts={appShortcuts}
			customizationStore={TEAMS_CUSTOMIZATION_STORE}
			disableMenuCustomization
			forYou={forYouConfig}
		/>
	);
}

const teamsAppLabelMessage = defineMessage({
	id: 'townsquare.teams.app.label',
	defaultMessage: 'Teams',
	description: 'Label for the app when shown in the global side navigation.',
});
